/** @format **/
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import PaperedTable from '../../components/Table/PaperedTable';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import TableCell from '@material-ui/core/TableCell';
import { TableBody, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { history } from '../../core/history';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { FilterManager } from '../../modules/filter/FilterManager';
import { MvnoBlockListCollectionInterface } from '../../interfaces/collections/MvnoBlockListCollectionInterface';
import MvnoBlockListItem from './MvnoBlockListItem';
import { BlockActions, BlockReducerType } from '../../modules/block/blockReducers';
import { useFilterParseToArray } from '../../modules/lib/hooks/useFilterParseToArray';
import { usePlatformResellerList } from '../../modules/lib/hooks/usePlatformResellerHooks';
import { useBlockList } from '../../modules/lib/hooks/useBlockList';
import { Auth } from '../../modules/auth/Auth';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import Pager from '../../components/Adescom/PagerRange';
import { MvnoBlockListCloneDialog } from './MvnoBlockListCloneDialog';
import MvnoBlockListInterface from '../../interfaces/platform/MvnoBlockListInterface';
import { HeaderCheckBoxSelectListElement } from '../lib/search/listTableHeader/HeaderCheckBoxSelectList';

type MvnoBlockListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [{ name: 'listTypeId[]', value: 1 }];
const setDefaultOrders = (): Array<Order> => [{ name: 'blockName', value: 'asc' }];

const MvnoBlockList: FunctionComponent<MvnoBlockListType> = ({ classes, showMainLoader }): React.ReactElement => {
    const dispatch = useDispatch();
    const filterStack = useRef<Array<Filter>>(setDefaultFilters());
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const { type, blockList } = useSelector((state: { blockReducers: BlockReducerType }) => state.blockReducers);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(true);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const blockListHeaders = useBlockList(Auth.getUserPlatformResellerId());
    const [cloneOpen, setCloneOpen] = useState(false);
    const [blockCloneName, setBlockCloneName] = useState<string | null>(null);
    const [blockCloneId, setBlockCloneId] = useState<number | null>(null);
    const [blockListPrepared, setBlockListPrepared] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [platformResellerListHeader, setPlatformResellerListHeader] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);

    filterStack.current = useFilterParseToArray(filters, false);

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_block_lists')),
                BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
                filterStack.current,
                orders,
                (data: MvnoBlockListCollectionInterface) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    dispatch({
                        type: BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
                        blockList: data,
                    } as BlockReducerType);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filterStack, orders, reload, showMainLoader]);

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
            (data: MvnoBlockListCollectionInterface): void => {
                dispatch({
                    type: BlockActions.BLOCK_LIST_LOAD_GLOBAL_LIST,
                    blockList: data,
                } as BlockReducerType);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        setReload(true);
    };
    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const viewCallback = (iri: { uri: string }, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/block-edit' + iri.uri));
    };

    const viewErrorCallback = (iri: { uri: string }, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/block-error-management'));
    };

    const lockCallback = (iri: { uri: string; lockFlag: boolean }, event: any): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(iri.uri),
            'mvno_block_list_lock_block',
            {
                lockFlag: iri.lockFlag,
            },
            (data: MvnoBlockListInterface): void => {
                setReload(true);
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    useEffect(() => {
        if (blockListHeaders && !blockListPrepared) {
            const blockList = blockListHeaders['hydra:member'].map((el: any) => {
                return { name: el.blockName, value: el.blockName };
            });

            setBlockListPrepared(blockList);
        }
    }, [blockListHeaders, blockListPrepared]);

    const platformResellerList = usePlatformResellerList();
    useEffect(() => {
        const headers: Array<HeaderCheckBoxSelectListElement> = [];
        if (platformResellerList && !platformResellerListHeader) {
            for (let i = 0; i < platformResellerList['hydra:member'].length; i++) {
                headers.push({
                    name: platformResellerList['hydra:member'][i].mainname,
                    value: platformResellerList['hydra:member'][i].id,
                });
            }
            setPlatformResellerListHeader(headers);
        }
    }, [platformResellerList, platformResellerListHeader]);

    if (!platformResellerListHeader) {
        return <div></div>;
    }

    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                <CustomTextButtom
                                    callback={viewErrorCallback}
                                    callbackParam={{}}
                                    color="primary"
                                    label="prov:error_management.error_management"
                                    size="lg"
                                />
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:block.block_name"
                                    textAlign="left"
                                    fieldName="blockName"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={blockListPrepared}
                                    element
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:list.partner"
                                    textAlign="left"
                                    fieldName="platformResellerId"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={platformResellerListHeader}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:block.global"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="defaultGlobal"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:block.created_at"
                                    fieldName="createdAt"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={true}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:block.deleted_at"
                                    fieldName="deletedAt"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="asc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    dateFromTo={true}
                                    filters={filters}
                                    sort={orders}
                                    breakLine={true}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                    nullable={true}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:features.available"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="deleted"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {blockList &&
                            blockList['hydra:member'].map((listItem) => {
                                return (
                                    <MvnoBlockListItem
                                        key={listItem.id}
                                        item={listItem}
                                        activeRow={activeRow}
                                        setActiveRow={setActiveRow}
                                        viewCallback={viewCallback}
                                        lockCallback={lockCallback}
                                        platformResellerCollection={platformResellerList}
                                        openClone={(blockName: string, blockId: number): void => {
                                            setBlockCloneId(blockId);
                                            setBlockCloneName(blockName);
                                            setCloneOpen(true);
                                        }}
                                    />
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {blockList && blockList['hydra:view'] && <Pager callback={paginationCallbackFunction} views={blockList} />}
                                <MvnoBlockListCloneDialog
                                    open={cloneOpen}
                                    blockName={blockCloneName}
                                    blockId={blockCloneId}
                                    closeCallback={(): void => setCloneOpen(false)}
                                    reloadCallback={(): void => {
                                        setCloneOpen(false);
                                        setBlockCloneName(null);
                                        setReload(true);
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
            </GridItem>
        </GridContainer>
    );
};
export default withStyles(oplAppStyle)(MvnoBlockList);
